import type { BookingMessages } from "@/i18n/types";
import type {
  CompanionFieldKey,
  CompanionFieldsConfig,
  CompanionFieldsConfigByRole,
  CompanionFormData,
  CompanionRole,
} from "@/lib/companionFields";
import {
  companionFieldConfigForRole,
  companionSlotLabel,
  emptyCompanion,
} from "@/lib/companionFields";
import type { PrimaryGuestFieldsConfig, PrimaryGuestFormData } from "@/lib/primaryGuestFields";

export const CHILD_MIN_AGE = 2;
export const CHILD_MAX_AGE = 14;
export const ADULT_MIN_AGE = 15;
export const PRIMARY_MIN_AGE = 18;

export type PartyAgeAdjustment = {
  adults: number;
  children: number;
  companions: CompanionFormData[];
};

function parseIsoDate(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

export function ageOnDate(birthDate: string, onDate: string): number | null {
  const birth = parseIsoDate(birthDate);
  const on = parseIsoDate(onDate);
  if (!birth || !on) return null;
  let age = on.getFullYear() - birth.getFullYear();
  const monthDiff = on.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && on.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

export function childCountsAsAdultOnArrival(birthDate: string, arrivalDate: string): boolean {
  const age = ageOnDate(birthDate, arrivalDate);
  return age !== null && age >= ADULT_MIN_AGE;
}

export function adultCountsAsChildOnArrival(birthDate: string, arrivalDate: string): boolean {
  const age = ageOnDate(birthDate, arrivalDate);
  return age !== null && age >= CHILD_MIN_AGE && age <= CHILD_MAX_AGE;
}

function dobModeActive(mode: string): boolean {
  return mode !== "off";
}

export function birthDateAgeValidationActive(
  primaryConfig: PrimaryGuestFieldsConfig,
  companionConfigs: CompanionFieldsConfigByRole,
): boolean {
  if (dobModeActive(primaryConfig.date_of_birth)) return true;
  return (
    dobModeActive(companionConfigs.adult.date_of_birth) ||
    dobModeActive(companionConfigs.child.date_of_birth)
  );
}

function validateAgeOnArrival(
  age: number,
  role: CompanionRole | "primary",
  messages: BookingMessages,
): string | null {
  if (role === "primary") {
    if (age < PRIMARY_MIN_AGE) return messages.validation.primaryAgeMin;
    return null;
  }
  if (role === "child") {
    if (age < CHILD_MIN_AGE) return messages.validation.childAgeMin;
    if (age >= ADULT_MIN_AGE) return null;
    if (age > CHILD_MAX_AGE) return messages.validation.childAgeRange;
    return null;
  }
  if (age < ADULT_MIN_AGE) return messages.validation.adultAgeMin;
  return null;
}

export function getPrimaryGuestBirthDateError(options: {
  birthDate: string;
  arrivalDate: string;
  dobMode: string;
  messages: BookingMessages;
}): string | null {
  const { birthDate, arrivalDate, dobMode, messages } = options;
  if (!dobModeActive(dobMode) || !arrivalDate.trim()) return null;
  const dob = birthDate.trim();
  if (!dob) return null;
  const age = ageOnDate(dob, arrivalDate);
  if (age === null) return messages.validation.invalidBirthDate;
  return validateAgeOnArrival(age, "primary", messages);
}

export function validateTravelerBirthDates(options: {
  arrivalDate: string;
  primaryGuestForm: PrimaryGuestFormData;
  primaryGuestFieldConfig: PrimaryGuestFieldsConfig;
  companions: CompanionFormData[];
  companionFieldConfigs: CompanionFieldsConfigByRole;
  messages: BookingMessages;
}): string | null {
  const {
    arrivalDate,
    primaryGuestForm,
    primaryGuestFieldConfig,
    companions,
    companionFieldConfigs,
    messages,
  } = options;

  if (!arrivalDate.trim()) return null;

  const primaryDobMode = primaryGuestFieldConfig.date_of_birth;
  if (dobModeActive(primaryDobMode)) {
    const dob = primaryGuestForm.date_of_birth.trim();
    if (primaryDobMode === "required" && !dob) {
      return messages.validation.primaryBirthDateRequired;
    }
    const primaryError = getPrimaryGuestBirthDateError({
      birthDate: dob,
      arrivalDate,
      dobMode: primaryDobMode,
      messages,
    });
    if (primaryError) return primaryError;
  }

  let adultIndex = 0;
  let childIndex = 0;
  for (const companion of companions) {
    const slotIndex = companion.role === "child" ? childIndex++ : adultIndex++;
    const config = companionFieldConfigForRole(companionFieldConfigs, companion.role);
    const dobMode = config.date_of_birth;
    if (!dobModeActive(dobMode)) continue;

    const dob = companion.date_of_birth.trim();
    if (dobMode === "required" && !dob) {
      const slot = companionSlotLabel(companion.role, slotIndex, messages);
      return messages.validation.companionBirthDateRequired(slot);
    }
    if (!dob) continue;

    const age = ageOnDate(dob, arrivalDate);
    if (age === null) {
      const slot = companionSlotLabel(companion.role, slotIndex, messages);
      return messages.validation.companionInvalidBirthDate(slot);
    }
    if (companion.role === "child" && childCountsAsAdultOnArrival(dob, arrivalDate)) {
      continue;
    }
    if (companion.role === "adult" && adultCountsAsChildOnArrival(dob, arrivalDate)) {
      continue;
    }
    const error = validateAgeOnArrival(age, companion.role, messages);
    if (error) {
      const slot = companionSlotLabel(companion.role, slotIndex, messages);
      return `${slot}: ${error}`;
    }
  }

  return null;
}

export function syncCompanionsToPartyCounts(
  companions: CompanionFormData[],
  adultsCount: number,
  childrenCount: number,
): CompanionFormData[] {
  const expectedAdultSlots = Math.max(0, adultsCount - 1);
  const expectedChildSlots = childrenCount;
  const adultData = companions.filter((companion) => companion.role === "adult");
  const childData = companions.filter((companion) => companion.role === "child");
  const result: CompanionFormData[] = [];
  for (let index = 0; index < expectedAdultSlots; index += 1) {
    result.push(
      adultData[index] ? { ...adultData[index], role: "adult" } : emptyCompanion("adult"),
    );
  }
  for (let index = 0; index < expectedChildSlots; index += 1) {
    result.push(
      childData[index] ? { ...childData[index], role: "child" } : emptyCompanion("child"),
    );
  }
  return result;
}

export function applyBirthDatePartyAdjustments(options: {
  arrivalDate: string;
  adults: number;
  children: number;
  companions: CompanionFormData[];
  companionFieldConfigs: CompanionFieldsConfigByRole;
}): PartyAgeAdjustment {
  const { arrivalDate, companions, companionFieldConfigs } = options;
  let adults = options.adults;
  let children = options.children;
  const adjustedCompanions = companions.map((companion) => ({ ...companion }));

  if (!arrivalDate.trim()) {
    return { adults, children, companions: adjustedCompanions };
  }

  let upgrades = 0;
  let downgrades = 0;
  for (const companion of adjustedCompanions) {
    const config = companionFieldConfigForRole(companionFieldConfigs, companion.role);
    if (!dobModeActive(config.date_of_birth)) continue;
    const dob = companion.date_of_birth.trim();
    if (!dob) continue;

    if (companion.role === "child" && childCountsAsAdultOnArrival(dob, arrivalDate)) {
      companion.role = "adult";
      upgrades += 1;
      continue;
    }
    if (companion.role === "adult" && adultCountsAsChildOnArrival(dob, arrivalDate)) {
      companion.role = "child";
      downgrades += 1;
    }
  }

  adults = Math.max(1, adults + upgrades - downgrades);
  children = Math.max(0, children - upgrades + downgrades);
  return {
    adults,
    children,
    companions: syncCompanionsToPartyCounts(adjustedCompanions, adults, children),
  };
}

export function isDeprecatedAgeField(key: CompanionFieldKey): boolean {
  return key === "age";
}

export function visibleCompanionFieldKeys(
  config: CompanionFieldsConfig,
): CompanionFieldKey[] {
  return (Object.keys(config) as CompanionFieldKey[]).filter(
    (key) => config[key] !== "off" && !isDeprecatedAgeField(key),
  );
}
